import { useEffect, useId, useMemo, useRef, useState } from "react";
import * as api from "../../api";

const iniciales = n => String(n || "").split(" ").map(x => x[0]).join("").slice(0, 2);
const sinAcentos = s => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Agrupa por equipo: primero los equipos de quien manda, luego el resto por nombre y «Sin equipo» al final.
export function agruparPorEquipo(personas, equiposMios = []) {
  const mapa = new Map();
  for (const p of personas) {
    const k = p.equipo || "";
    if (!mapa.has(k)) mapa.set(k, []);
    mapa.get(k).push(p);
  }
  const mios = new Set(equiposMios);
  const claves = [...mapa.keys()].sort((a, b) => {
    if (!a) return 1;
    if (!b) return -1;
    const ma = mios.has(a), mb = mios.has(b);
    if (ma !== mb) return ma ? -1 : 1;
    return a.localeCompare(b, "es");
  });
  return claves.map(k => ({ equipo: k || "Sin equipo", suyo: !!k && mios.has(k), personas: mapa.get(k) }));
}

// Elegir a quien se manda la tarea: combobox con buscador (nombre o equipo), personas agrupadas por equipo,
// teclado completo y aviso si hay mas de las que caben. Lo cargado se filtra en el cliente; si el servidor
// tiene mas de lo cargado (total > lista), al escribir se le pregunta a el.
export default function SelectorDestinatario({ receptores = [], total = null, equiposMios = [], value, onChange, inputId }) {
  const id = useId();
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const [remotos, setRemotos] = useState(null);   // resultado de ?q= cuando lo cargado no es todo
  const caja = useRef(null);
  const faltan = Number.isFinite(total) && total > receptores.length;

  useEffect(() => {
    const texto = q.trim();
    if (!faltan || !texto) { setRemotos(null); return undefined; }
    let vivo = true;
    const t = setTimeout(() => {
      api.getReceptoresEncargo(texto).then(d => { if (vivo) setRemotos(d); }).catch(() => { if (vivo) setRemotos(null); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [q, faltan]);

  const { grupos, plano, hayMas } = useMemo(() => {
    const t = sinAcentos(q.trim());
    const base = remotos?.receptores
      || (t ? receptores.filter(r => sinAcentos(r.nombre).includes(t) || sinAcentos(r.equipo).includes(t)) : receptores);
    const g = agruparPorEquipo(base, equiposMios);
    const totalBusqueda = remotos ? remotos.total : (faltan && !t ? total : base.length);
    return { grupos: g, plano: g.flatMap(x => x.personas), hayMas: Math.max(0, (Number.isFinite(totalBusqueda) ? totalBusqueda : base.length) - base.length) };
  }, [q, receptores, remotos, equiposMios, faltan, total]);

  useEffect(() => { setActivo(0); }, [q, abierto]);
  useEffect(() => {
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const elegir = p => { onChange(p); setAbierto(false); setQ(""); };
  const opId = i => `${id}-op-${i}`;

  const teclado = e => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAbierto(true); setActivo(a => Math.min(plano.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActivo(a => Math.max(0, a - 1)); }
    else if (e.key === "Home" && abierto) { e.preventDefault(); setActivo(0); }
    else if (e.key === "End" && abierto) { e.preventDefault(); setActivo(plano.length - 1); }
    else if (e.key === "Enter" && abierto && plano[activo]) { e.preventDefault(); elegir(plano[activo]); }
    else if (e.key === "Escape" && abierto) { e.preventDefault(); e.stopPropagation(); setAbierto(false); }
  };

  if (value) {
    return (
      <div className="mt-sel-elegida">
        <span className="mt-av" aria-hidden="true">{iniciales(value.nombre)}</span>
        <span className="mt-sel-t"><b>{value.nombre}</b>{value.equipo && <small>{value.equipo}</small>}</span>
        <button type="button" className="mt-sel-x btn" aria-label={`Cambiar de persona: ${value.nombre}`}
          onClick={() => onChange(null)}>×</button>
      </div>
    );
  }

  let n = -1;
  return (
    <div className="mt-dest" ref={caja}>
      <input id={inputId} className="mt-inp" role="combobox" aria-expanded={abierto} aria-controls={`${id}-lista`}
        aria-autocomplete="list" aria-activedescendant={abierto && plano[activo] ? opId(activo) : undefined}
        placeholder="Buscar por nombre o por equipo…" value={q} autoComplete="off"
        onChange={e => { setQ(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)} onClick={() => setAbierto(true)} onKeyDown={teclado} />
      {abierto && (
        <div className="mt-dest-lista" id={`${id}-lista`} role="listbox" aria-label="Personas">
          {!plano.length && <p className="mt-sel-nota" role="status">Nadie coincide.</p>}
          {grupos.map(g => (
            <div key={g.equipo} role="group" aria-label={g.equipo}>
              <div className="mt-sel-gl" aria-hidden="true">{g.equipo}{g.suyo && <span> · tu equipo</span>}</div>
              {g.personas.map(p => {
                n += 1;
                const i = n;
                return (
                  <div key={p.id} id={opId(i)} role="option" aria-selected={i === activo}
                    className={"mt-dest-op" + (i === activo ? " act" : "")}
                    onMouseDown={e => e.preventDefault()} onClick={() => elegir(p)} onMouseEnter={() => setActivo(i)}>
                    <span className="mt-av" aria-hidden="true">{iniciales(p.nombre)}</span>
                    <span className="mt-sel-t"><b>{p.nombre}</b></span>
                  </div>
                );
              })}
            </div>
          ))}
          {hayMas > 0 && <p className="mt-sel-nota" role="status">Hay {hayMas} más: afina la búsqueda.</p>}
        </div>
      )}
    </div>
  );
}
